/* KPITecnicos.jsx  v2.0
   KPI de técnicos por sucursal y zona — Modo Austeridad · Tarea 3
   Tema claro consistente con AdminZonas.jsx */
import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import {
  Users, Building2, MapPin, Clock, Percent, Search, Calendar,
  Edit2, X, Check, Loader2, BarChart3, AlertTriangle, Filter, Layers, Briefcase, User,
} from "lucide-react";
import api from "../lib/api";
import MultiSelect from "../components/MultiSelect";
import Layout from "../components/Layout";

function CapacityBar({ pct }) {
  const clampPct = Math.min(pct, 100);
  const color = pct === 0 ? "bg-gray-300" : pct <= 70 ? "bg-emerald-500" : pct <= 90 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2 min-w-[140px]">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${clampPct}%` }} />
      </div>
      <span className={`text-xs font-mono font-semibold min-w-[42px] text-right ${pct === 0 ? "text-gray-400" : pct <= 70 ? "text-emerald-600" : pct <= 90 ? "text-amber-600" : "text-red-600"}`}>
        {pct}%
      </span>
    </div>
  );
}

function EditTechModal({ tech, shiftTemplates, allZones, allBranches, onClose, onSaved }) {
  // v101.8.0 · Zonas y turno solo se muestran (fuentes únicas: Gestión Geográfica / Horarios de Técnicos)
  const selectedZones = tech.zone_ids || [];
  const [selectedBranch, setSelectedBranch] = useState(tech.branch_id || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      // v101.8.0 · Aquí solo se cambia la sucursal. Zonas → «Gestión Geográfica»;
      //            turno → «Horarios de Técnicos» (fuentes únicas).
      await api.put(`/v1/analytics/tech/${tech.tech_id}/zones`, {
        branch_id: selectedBranch || null,
      });
      toast.success("Técnico actualizado");
      onSaved();
    } catch (e) {
      toast.error("Error al guardar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white border border-gray-200 rounded-xl shadow-xl w-full max-w-lg mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-tech-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{tech.tech_name}</h3>
            <p className="text-sm text-gray-500">{tech.tech_email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="close-edit-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <label className="block text-sm font-medium text-gray-700 mb-1.5">Sucursal</label>
        <select
          value={selectedBranch}
          onChange={(e) => setSelectedBranch(e.target.value)}
          className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          data-testid="edit-branch-select"
        >
          <option value="">Sin sucursal</option>
          {allBranches.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1.5">Zonas asignadas</label>
        <div className="flex flex-wrap gap-1.5 border border-gray-200 rounded-lg p-2 bg-gray-50 min-h-[38px]" data-testid="edit-zones-readonly">
          {(selectedZones || []).length === 0 ? (
            <span className="text-xs text-gray-400 italic">Sin zonas</span>
          ) : selectedZones.map((zid) => {
            const z = allZones.find((x) => String(x.id) === String(zid));
            return <span key={zid} className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200">{z ? z.name : String(zid).slice(0, 8)}</span>;
          })}
        </div>
        <p className="text-[11px] text-gray-500 mt-1">Se asignan en <a href="/admin/zonas" className="underline">Gestión Geográfica</a> → zona → Técnicos.</p>

        <label className="block text-sm font-medium text-gray-700 mb-1.5 mt-4">Turno / Horario</label>
        <div className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-gray-50" data-testid="edit-shift-readonly">
          {tech.shift_name || "Sin turno"}
        </div>
        <p className="text-[11px] text-gray-500 mt-1 mb-5">Se asigna en <a href="/admin/horarios" className="underline">Horarios de Técnicos</a>.</p>

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50" data-testid="cancel-edit-btn">
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            data-testid="save-edit-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function KPITecnicos() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [branchFilter, setBranchFilter] = useState([]);
  const [zoneFilter, setZoneFilter] = useState([]);
  // v101.227.0 · Filtros por técnico, contratista y tipo de solución
  const [techFilter, setTechFilter] = useState([]);
  const [ctrFilter, setCtrFilter] = useState([]);
  const [solFilter, setSolFilter] = useState([]);
  const [allTechs, setAllTechs] = useState([]);
  const [mode, setMode] = useState("today");
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));

  const [editTech, setEditTech] = useState(null);
  const [shiftTemplates, setShiftTemplates] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (branchFilter.length) params.set("branch_ids", branchFilter.join(","));
      if (zoneFilter.length) params.set("zone_ids", zoneFilter.join(","));
      if (techFilter.length) params.set("tech_ids", techFilter.join(","));
      if (ctrFilter.length) params.set("contratista", ctrFilter.join("|"));
      if (solFilter.length) params.set("solution_node_id", solFilter.join(","));
      params.set("mode", mode);
      if (mode === "range") {
        params.set("date_from", dateFrom);
        params.set("date_to", dateTo);
      }
      const r = await api.get(`/v1/analytics/tech-capacity?${params.toString()}`);
      setData(r.data);
      if (!techFilter.length && !ctrFilter.length) {   // lista completa para el selector de técnicos
        setAllTechs((r.data?.items || []).map((t) => ({ value: t.tech_id, label: t.tech_name, sublabel: t.contratista || t.branch_name })));
      }
    } catch (e) {
      toast.error("Error cargando KPI: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [branchFilter, zoneFilter, techFilter, ctrFilter, solFilter, mode, dateFrom, dateTo]);

  const fetchTemplates = async () => {
    try {
      const r = await api.get("/v1/analytics/shift-templates");
      setShiftTemplates(r.data.items || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchTemplates(); }, []);

  const branchOptions = useMemo(() =>
    (data?.branches || []).map((b) => ({ value: b.id, label: b.name })),
  [data?.branches]);

  const zoneOptions = useMemo(() => {
    let zones = data?.zones || [];
    if (branchFilter.length) {
      const set = new Set(branchFilter);
      zones = zones.filter((z) => set.has(z.branch_id));
    }
    return zones.map((z) => ({ value: z.id, label: z.name, sublabel: z.branch_name }));
  }, [data?.zones, branchFilter]);

  const solOptions = useMemo(() =>
    (data?.solution_types || []).map((t) => ({ value: t.id, label: t.path || t.name, sublabel: `${t.cnt || 0} OTs` })),
  [data?.solution_types]);
  const ctrOptions = useMemo(() =>
    (data?.contratistas || []).map((c) => ({ value: c.nombre, label: c.nombre, sublabel: c.cnt ? `${c.cnt} OTs` : "perfil" })),
  [data?.contratistas]);

  const filtered = useMemo(() => {
    if (!data?.items) return [];
    const q = search.toLowerCase().trim();
    if (!q) return data.items;
    return data.items.filter(
      (i) =>
        i.tech_name?.toLowerCase().includes(q) ||
        i.tech_email?.toLowerCase().includes(q) ||
        i.branch_name?.toLowerCase().includes(q) ||
        i.zones?.some((z) => z.name?.toLowerCase().includes(q))
    );
  }, [data?.items, search]);

  const branchSummary = useMemo(() => {
    if (!filtered.length) return [];
    const map = {};
    for (const t of filtered) {
      const bk = t.branch_id || "_none";
      if (!map[bk]) map[bk] = { branch_name: t.branch_name, techs: 0, ots: 0, avg_cap: 0, _caps: [] };
      map[bk].techs++;
      map[bk].ots += t.ot_count;
      map[bk]._caps.push(t.capacity_pct);
    }
    return Object.values(map).map((b) => ({
      ...b,
      avg_cap: b._caps.length ? Math.round(b._caps.reduce((a, c) => a + c, 0) / b._caps.length) : 0,
    })).sort((a, b) => b.avg_cap - a.avg_cap);
  }, [filtered]);

  const summary = data?.summary || {};

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-5" data-testid="kpi-tecnicos-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BarChart3 size={22} style={{ color: "#0A3747" }} />
            <div>
              <h1 className="text-xl font-bold" style={{ color: "#0A3747" }}>KPI Técnicos — Capacidad</h1>
              <p className="text-xs text-gray-500">Carga de trabajo por sucursal y zona</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Calendar className="w-4 h-4" />
            {mode === "today" ? "Hoy" : `${dateFrom} → ${dateTo}`}
          </div>
        </div>

        {/* Summary Cards */}
        {!loading && data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kpi-summary-cards">
            <SummaryCard icon={Users} label="Técnicos" value={summary.total_techs} bg="bg-blue-50" color="text-blue-600" />
            <SummaryCard icon={Clock} label="OTs Asignadas" value={summary.total_ots} bg="bg-indigo-50" color="text-indigo-600" />
            <SummaryCard icon={Percent} label="Cap. Promedio" value={`${summary.avg_capacity}%`} bg="bg-emerald-50" color="text-emerald-600" />
            <SummaryCard icon={AlertTriangle} label="Sobrecargados" value={summary.overloaded} bg="bg-red-50" color="text-red-600" subtitle=">90%" />
            <SummaryCard icon={Users} label="Inactivos" value={summary.idle} bg="bg-gray-50" color="text-gray-500" subtitle="0 OTs" />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-testid="kpi-filters">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Filter className="w-4 h-4" /> Filtros
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <MultiSelect
              label="Sucursales"
              options={branchOptions}
              selected={branchFilter}
              onChange={(v) => { setBranchFilter(v); setZoneFilter([]); }}
              placeholder="Todas"
              icon={<Building2 className="w-4 h-4" />}
              testId="filter-branch"
            />
            <MultiSelect
              label="Zonas"
              options={zoneOptions}
              selected={zoneFilter}
              onChange={setZoneFilter}
              placeholder="Todas"
              icon={<MapPin className="w-4 h-4" />}
              testId="filter-zone"
            />
            <MultiSelect
              label="Técnicos"
              options={allTechs}
              selected={techFilter}
              onChange={setTechFilter}
              placeholder="Todos"
              icon={<User className="w-4 h-4" />}
              testId="filter-tech"
            />
            <MultiSelect
              label="Contratistas"
              options={ctrOptions}
              selected={ctrFilter}
              onChange={setCtrFilter}
              placeholder="Todos"
              icon={<Briefcase className="w-4 h-4" />}
              testId="filter-contratista"
            />
            <MultiSelect
              label="Tipo de solución"
              options={solOptions}
              selected={solFilter}
              onChange={setSolFilter}
              placeholder="Todos"
              icon={<Layers className="w-4 h-4" />}
              testId="filter-solution"
            />
            <div>
              <label className="block text-xs text-gray-500 mb-1">Ventana temporal</label>
              <div className="flex gap-1">
                <button
                  onClick={() => setMode("today")}
                  className={`flex-1 px-3 py-2 text-xs rounded-lg border transition ${mode === "today" ? "bg-blue-50 border-blue-400 text-blue-700 font-medium" : "bg-white border-gray-300 text-gray-500 hover:border-gray-400"}`}
                  data-testid="mode-today-btn"
                >
                  Hoy
                </button>
                <button
                  onClick={() => setMode("range")}
                  className={`flex-1 px-3 py-2 text-xs rounded-lg border transition ${mode === "range" ? "bg-blue-50 border-blue-400 text-blue-700 font-medium" : "bg-white border-gray-300 text-gray-500 hover:border-gray-400"}`}
                  data-testid="mode-range-btn"
                >
                  Rango
                </button>
              </div>
            </div>
            {mode === "range" && (
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Desde</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-xs text-gray-900 focus:ring-2 focus:ring-blue-500"
                    data-testid="date-from"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Hasta</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-lg px-2.5 py-2 text-xs text-gray-900 focus:ring-2 focus:ring-blue-500"
                    data-testid="date-to"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, email, sucursal, zona..."
              className="w-full bg-white border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Branch Summary */}
        {branchSummary.length > 1 && (
          <div className="flex gap-2 overflow-x-auto" data-testid="branch-summary">
            {branchSummary.map((b, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-center flex-1 min-w-0">
                <div className="text-[10px] text-gray-500 truncate">{b.branch_name}</div>
                <div className="text-sm font-bold text-gray-900">{b.techs} <span className="text-[10px] font-normal text-gray-400">{b.ots} OTs</span></div>
                <CapacityBar pct={b.avg_cap} />
              </div>
            ))}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="kpi-table-container">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">Cargando KPI...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="kpi-table">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left">
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Técnico</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Sucursal</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Zonas</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Turno</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">OTs</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Horas Turno</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Horas OT</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">% Capacidad</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-12 text-center text-gray-400">
                        No se encontraron técnicos con los filtros seleccionados
                      </td>
                    </tr>
                  ) : (
                    filtered.map((t) => (
                      <tr key={t.tech_id} className="border-b border-gray-100 hover:bg-gray-50 transition" data-testid={`tech-row-${t.tech_id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 text-sm">{t.tech_name}</div>
                          <div className="text-xs text-gray-400">{t.tech_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-700">{t.branch_name}</span>
                        </td>
                        <td className="px-4 py-3">
                          {t.zones?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {t.zones.slice(0, 2).map((z) => (
                                <span key={z.id} className="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-700 border border-gray-200 truncate max-w-[100px]">
                                  {z.name}
                                </span>
                              ))}
                              {t.zones.length > 2 && (
                                <span className="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500 border border-gray-200">
                                  +{t.zones.length - 2}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Sin zona</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-600">{t.shift_name}</span>
                          {t.shift_hours_day > 0 && (
                            <span className="text-xs text-gray-400 ml-1">({t.shift_hours_day}h/día)</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-mono text-sm text-gray-900">{t.ot_count}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-mono text-sm text-gray-600">{t.total_shift_hours}h</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-mono text-sm text-gray-600">{t.estimated_hours}h</span>
                        </td>
                        <td className="px-4 py-3">
                          <CapacityBar pct={t.capacity_pct} />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setEditTech(t)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition"
                            title="Editar zonas y horario"
                            data-testid={`edit-tech-${t.tech_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Edit Modal */}
        {editTech && (
          <EditTechModal
            tech={editTech}
            shiftTemplates={shiftTemplates}
            allZones={data?.zones || []}
            allBranches={data?.branches || []}
            onClose={() => setEditTech(null)}
            onSaved={() => { setEditTech(null); fetchData(); }}
          />
        )}
      </div>
    </Layout>
  );
}

function SummaryCard({ icon: Icon, label, value, bg, color, subtitle }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3">
      <div className={`p-2 rounded-lg ${bg} ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-lg font-bold text-gray-900 leading-tight">{value}</div>
        <div className="text-xs text-gray-500">{label} {subtitle && <span className="text-gray-400">({subtitle})</span>}</div>
      </div>
    </div>
  );
}
